import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Divider,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { post } from "../../services";
import {
  ADD_INVOICE_SERIES_SETTINGS,
  GET_INVOICE_SERIES_SETTINGS,
} from "../../graphql";

const FieldAndValue = ({ field, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
    <Typography fontSize={14} fontWeight={600}>
      {field}
    </Typography>
    <Typography fontSize={14}>{value}</Typography>
  </Stack>
);

/* ------------------------------------------------------------------
   Component for each series section
------------------------------------------------------------------ */
const SeriesEditor = ({ label, data, onChange, disabled }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle1" fontWeight={600}>
      {label}
    </Typography>

    {!disabled ? (
      <>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Prefix"
            size="small"
            value={data?.prefix || ""}
            onChange={(e) => onChange("prefix", e.target.value)}
            sx={{ flex: 1 }}
          />

          <TextField
            label="Start Number"
            size="small"
            type="number"
            value={data?.start_number || ""}
            onChange={(e) => onChange("start_number", Number(e.target.value))}
            sx={{ flex: 1 }}
          />
        </Stack>
      </>
    ) : (
      <>
        <FieldAndValue field="Prefix" value={data?.prefix || "-"} />
        <FieldAndValue field="Start Number" value={data?.start_number ?? "-"} />
        <FieldAndValue
          field="Current Number"
          value={data?.current_number ?? "-"}
        />
      </>
    )}

    <Divider sx={{ mt: 1 }} />
  </Stack>
);

/* ---------------------------------------------------
   MAIN COMPONENT
--------------------------------------------------- */
export const EditableInvoiceSeriesAccordion = ({ hotelId, colors }) => {
  const HOTEL_SERIES = ["B2C", "B2B", "Complimentary", "Proforma"];

  const [form, setForm] = useState({
    hotel_billing: {},
    restaurant_billing: {},
    activity_billing: {},
    advance_receipt: {},
  });

  const [dbSnapshot, setDbSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ----------------------------------------------------
      Fetch Data
  ----------------------------------------------------- */
  const loadSeries = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const apiResponse = await post("", {
        query: GET_INVOICE_SERIES_SETTINGS,
        variables: { hotel_id: hotelId },
      });

      const data = apiResponse?.data?.data?.get_invoice_series_settings?.data;

      if (!data) toast.error("No invoice series found. Please configure.");

      setForm({
        hotel_billing: data?.hotel_billing || {},
        restaurant_billing: data?.restaurant_billing || {},
        activity_billing: data?.activity_billing || {},
        advance_receipt: data?.advance_receipt || {},
      });

      setDbSnapshot({
        hotel_billing: data?.hotel_billing || {},
        restaurant_billing: data?.restaurant_billing || {},
        activity_billing: data?.activity_billing || {},
        advance_receipt: data?.advance_receipt || {},
      });
    } catch (err) {
      toast.error("Failed to load invoice series.");
      console.error(err);
    }

    setLoading(false);
  };

  /* ----------------------------------------------------
      Save Data
  ----------------------------------------------------- */
  const saveSeries = async () => {
    setSaving(true);

    try {
      // Remove current_number before saving
      const sanitize = (obj) => {
        if (!obj) return obj;
        const { current_number, ...rest } = obj;
        return rest;
      };

      const payload = {
        hotel_billing: Object.fromEntries(
          Object.entries(form.hotel_billing).map(([k, v]) => [k, sanitize(v)])
        ),
        restaurant_billing: sanitize(form.restaurant_billing),
        activity_billing: sanitize(form.activity_billing),
        advance_receipt: sanitize(form.advance_receipt),
      };

      await post("", {
        query: ADD_INVOICE_SERIES_SETTINGS,
        variables: payload,
      });

      toast.success("Invoice series updated successfully.");

      setEditMode(false);
      setDbSnapshot(form);
    } catch (err) {
      toast.error("Save failed.");
      console.error(err);
    }

    setSaving(false);
  };

  /* ----------------------------------------------------
      Cancel Edit
  ----------------------------------------------------- */
  const cancelEdit = () => {
    if (dbSnapshot) setForm(dbSnapshot);
    setEditMode(false);
  };

  /* ----------------------------------------------------
      Handlers
  ----------------------------------------------------- */
  const handleHotelChange = (seriesKey, field, value) => {
    if (!editMode) return;

    setForm((prev) => ({
      ...prev,
      hotel_billing: {
        ...prev.hotel_billing,
        [seriesKey]: {
          ...prev.hotel_billing?.[seriesKey],
          [field]: value,
        },
      },
    }));
  };

  const handleRestaurantChange = (field, value) => {
    if (!editMode) return;
    setForm((prev) => ({
      ...prev,
      restaurant_billing: {
        ...prev.restaurant_billing,
        [field]: value,
      },
    }));
  };

  const handleActivityChange = (field, value) => {
    if (!editMode) return;
    setForm((prev) => ({
      ...prev,
      activity_billing: {
        ...prev.activity_billing,
        [field]: value,
      },
    }));
  };

  const handleAdvanceReceiptChange = (field, value) => {
    if (!editMode) return;
    setForm((prev) => ({
      ...prev,
      advance_receipt: {
        ...prev.advance_receipt,
        [field]: value,
      },
    }));
  };

  /* ----------------------------------------------------
      UI RENDER
  ----------------------------------------------------- */
  return (
    <Accordion
      onChange={(e, expanded) => expanded && loadSeries()}
      sx={{ mt: 1, backgroundColor: colors.primary[400] }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="h4"
          fontWeight={600}
          color={colors.greenAccent[600]}
        >
          Billing – Invoice Series
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={22} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* HOTEL BILLING */}
            <Stack>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                Hotel Billing
              </Typography>

              {HOTEL_SERIES.map((series) => (
                <SeriesEditor
                  key={series}
                  label={series}
                  data={form.hotel_billing?.[series]}
                  disabled={!editMode}
                  onChange={(field, value) =>
                    handleHotelChange(series, field, value)
                  }
                />
              ))}
            </Stack>

            {/* RESTAURANT */}
            <Stack>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                Restaurant Billing
              </Typography>

              <SeriesEditor
                label="Restaurant"
                data={form.restaurant_billing}
                disabled={!editMode}
                onChange={(field, value) =>
                  handleRestaurantChange(field, value)
                }
              />
            </Stack>

            {/* ACTIVITY */}
            <Stack>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                Activity Billing
              </Typography>

              <SeriesEditor
                label="Activity"
                data={form.activity_billing}
                disabled={!editMode}
                onChange={(field, value) => handleActivityChange(field, value)}
              />
            </Stack>

            {/* ADVANCE RECEIPT - NEW SECTION */}
            <Stack>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                Advance Receipt
              </Typography>

              <SeriesEditor
                label="Advance Receipt"
                data={form.advance_receipt}
                disabled={!editMode}
                onChange={(field, value) =>
                  handleAdvanceReceiptChange(field, value)
                }
              />
            </Stack>

            {/* ACTION BUTTONS */}
            {!editMode ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditMode(true)}
                sx={{ width: 120 }}
              >
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={saving}
                  onClick={saveSeries}
                  sx={{ width: 120 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={saving}
                  sx={{ width: 120 }}
                  onClick={cancelEdit}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
